import { useRef, useState } from 'react';

import { renderMarkdown } from '../../../common/components/markdown/markdown';

// The editor of a plan's text, with three views of the same value.
//
//   split    the source on the left and what it renders on the right — THE
//            DEFAULT, because a plan is read by people who never see the
//            markdown, and writing it blind until a tab is switched is how a
//            broken heading reaches the reviewer
//   write    the source alone, for long typing on a narrow screen
//   preview  the rendered text alone, to read it the way the reviewer will
//
// Controlled: it holds the view and nothing else. The text belongs to the
// caller, which diffs it against what was read — a copy kept here would be a
// second draft that can drift from the one that gets saved.
//
// `children` goes in the toolbar, after the view switch, for what each caller
// adds there (the template button, the unsaved-changes mark).

const EDITOR_VIEWS = [
    { value: 'write',   label: 'Escribir' },
    { value: 'split',   label: 'Dividida' },
    { value: 'preview', label: 'Vista previa' },
];

const DEFAULT_EDITOR_VIEW = 'split';

export default function MarkdownEditor({
    value, onChange, disabled = false, rows = 24, label = 'Contenido del plan', children,
}) {
    const [view, setView] = useState(DEFAULT_EDITOR_VIEW);
    const previewRef = useRef(null);

    // In the split view the preview follows the source by PROPORTION, not by
    // line: rendered headings and lists are taller than their markdown, so the
    // same line number is a different height on each side. Proportional keeps
    // the part being written in sight without pretending to be exact.
    const followSource = (event) => {
        const pane = previewRef.current;
        if (view !== 'split' || !pane) return;

        const source = event.currentTarget;
        const range = source.scrollHeight - source.clientHeight;
        const ratio = range > 0 ? source.scrollTop / range : 0;
        pane.scrollTop = ratio * (pane.scrollHeight - pane.clientHeight);
    };

    const source = (
        <textarea
            className={`plans__textarea${view === 'split' ? ' plans__textarea--pane' : ''}`}
            value={value}
            onChange={e => onChange(e.target.value)}
            onScroll={followSource}
            rows={rows}
            spellCheck
            aria-label={label}
            disabled={disabled}
        />
    );

    const rendered = (
        <article
            ref={previewRef}
            className={`md${view === 'split' ? ' plans__preview-pane' : ''}`}
            aria-label="Vista previa"
        >
            {renderMarkdown(value) ?? (
                <p className="plans__note">Todavía no hay contenido.</p>
            )}
        </article>
    );

    return (
        <div className="plans__editor">
            <div className="plans__toolbar">
                <div className="plans__views" role="group" aria-label="Vista del editor">
                    {EDITOR_VIEWS.map(option => (
                        <button
                            key={option.value}
                            type="button"
                            className={`plans__tab ${view === option.value ? 'plans__tab--active' : ''}`}
                            onClick={() => setView(option.value)}
                            aria-pressed={view === option.value}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                {children}
            </div>

            {view === 'write' && source}
            {view === 'preview' && rendered}
            {view === 'split' && (
                <div className="plans__split">
                    {source}
                    {rendered}
                </div>
            )}
        </div>
    );
}
